import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Clock, DollarSign, Target, TrendingUp, MessageSquare, Rocket, ChevronRight, Shield, Flame, Heart, AlertTriangle, Info, Trophy, PenLine, FileText, Milestone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { projectsAPI, socialAPI, guidanceAPI, communityAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ShareButton from '../components/shared/ShareButton';
import WatchButton from '../components/guidance/WatchButton';
import NextActionCards from '../components/guidance/NextActionCards';
import Bridges from '../components/guidance/Bridges';
import { useSmartNotify } from '../components/guidance/SmartNotificationProvider';
import { toast } from 'sonner';

const QUICK_AMOUNTS = [1, 5, 10, 25, 50, 100];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { triggerSmartNotify } = useSmartNotify();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investAmount, setInvestAmount] = useState('10');
  const [selectedTier, setSelectedTier] = useState('');
  const [investMsg, setInvestMsg] = useState('');
  const [investing, setInvesting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [roi, setRoi] = useState(null);
  const [showROI, setShowROI] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [trustBadges, setTrustBadges] = useState([]);
  const [trustScore, setTrustScore] = useState(0);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateContent, setUpdateContent] = useState('');
  const [updateType, setUpdateType] = useState('general');
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalBackers, setTotalBackers] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, commRes, updRes, trustRes, lbRes] = await Promise.all([
          projectsAPI.get(id),
          socialAPI.getComments('project', id),
          projectsAPI.getUpdates(id).catch(() => ({ data: [] })),
          projectsAPI.getTrust(id).catch(() => ({ data: { badges: [], trust_score: 0 } })),
          communityAPI.backerLeaderboard(id).catch(() => ({ data: { leaderboard: [], total_backers: 0 } })),
        ]);
        setProject(projRes.data);
        setComments(commRes.data);
        setUpdates(updRes.data || []);
        setTrustBadges(trustRes.data?.badges || []);
        setTrustScore(trustRes.data?.trust_score || 0);
        setLeaderboard(lbRes.data?.leaderboard || []);
        setTotalBackers(lbRes.data?.total_backers || 0);
        if (projRes.data?.title) {
          guidanceAPI.trackActivity('view', 'project', id, projRes.data.title).catch(() => {});
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  // Fetch ROI when amount changes
  useEffect(() => {
    const amt = parseFloat(investAmount);
    if (!amt || amt < 1 || !id) return;
    const timeout = setTimeout(async () => {
      try {
        const res = await projectsAPI.roi(id, amt);
        setRoi(res.data);
      } catch { /* */ }
    }, 300);
    return () => clearTimeout(timeout);
  }, [investAmount, id]);

  const handleInvest = async () => {
    const amt = parseFloat(investAmount);
    if (!amt || amt < 1) { toast.error('Minimum $1'); return; }
    setInvesting(true);
    try {
      const res = await projectsAPI.invest(id, { amount: amt, tier_name: selectedTier, message: investMsg });
      toast.success(res.data.message);
      setProject(prev => ({ ...prev, current_funding: res.data.new_funding, backer_count: (prev.backer_count || 0) + 1 }));
      setInvestAmount('10');
      setInvestMsg('');
      // Trigger smart notification — suggest next action
      triggerSmartNotify('invested', 'project', id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Investment failed'); }
    finally { setInvesting(false); }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await socialAPI.addComment({ content_type: 'project', content_id: id, text: newComment });
      setComments([res.data, ...comments]);
      setNewComment('');
      triggerSmartNotify('commented', 'project', id);
    } catch { toast.error('Failed to comment'); }
  };

  const handlePostUpdate = async () => {
    if (!updateTitle.trim() || !updateContent.trim()) { toast.error('Title and content required'); return; }
    setPostingUpdate(true);
    try {
      const res = await projectsAPI.createUpdate(id, { title: updateTitle, content: updateContent, update_type: updateType });
      setUpdates([res.data, ...updates]);
      setUpdateTitle(''); setUpdateContent(''); setShowUpdateForm(false);
      toast.success('Update posted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post update'); }
    finally { setPostingUpdate(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!project) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><p className="text-zinc-500">Project not found</p></div>;

  const pct = Math.min(100, (project.current_funding / project.funding_goal) * 100);
  const deadline = new Date(project.deadline);
  const daysLeft = Math.max(0, Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)));
  const score = project.discovery_score || 0;

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="project-detail-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/projects" className="text-zinc-500 hover:text-white transition-colors" data-testid="back-to-projects"><ArrowLeft size={24} /></Link>
          <div className="flex items-center gap-3">
            <WatchButton entityType="project" entityId={id} size="md" />
            <ShareButton contentType="product" contentId={id} title={project.title} />
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 bg-zinc-800 text-zinc-300 text-xs font-bold rounded-full uppercase">{project.genre}</span>
            {project.status === 'funded' && <span className="px-3 py-1 bg-[#b8ff00]/10 text-[#b8ff00] text-xs font-bold rounded-full">FUNDED</span>}
            {score > 0 && (
              <span className="px-3 py-1 bg-[#b8ff00]/10 border border-[#b8ff00]/20 text-[#b8ff00] text-xs font-bold rounded-full flex items-center gap-1">
                <Target size={11} /> Score: {score}
              </span>
            )}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-1" data-testid="project-title">{project.title}</h1>
          <p className="text-sm text-zinc-500">by <Link to={`/artist/${project.creator_id}`} className="text-[#b8ff00] hover:underline">{project.creator_name}</Link></p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6">
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{project.description}</p>
            </div>

            {/* Investment Tiers */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6">
              <h2 className="font-bold text-lg text-white mb-4">Backing Tiers</h2>
              <div className="space-y-3">
                {project.tiers?.map((tier, i) => (
                  <button key={i} onClick={() => { setSelectedTier(tier.name); setInvestAmount(String(tier.amount)); }}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedTier === tier.name ? 'border-[#b8ff00] bg-[#b8ff00]/5' : 'border-zinc-700 hover:border-zinc-600'}`}
                    data-testid={`tier-${tier.name}`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-white">{tier.name}</span>
                      <span className="font-bold text-[#b8ff00]">{formatCurrency(tier.amount)}</span>
                    </div>
                    <p className="text-xs text-zinc-500">{tier.perks}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                      <span>{tier.backers} backer{tier.backers !== 1 ? 's' : ''}</span>
                      {tier.limit > 0 && <span className="text-amber-400">{tier.limit - tier.backers} left</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ROI Calculator */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6" data-testid="roi-calculator">
              <button onClick={() => setShowROI(!showROI)} className="w-full flex items-center justify-between">
                <h2 className="font-bold text-lg text-white flex items-center gap-2"><TrendingUp size={18} className="text-[#b8ff00]" /> What If Calculator</h2>
                <ChevronRight size={18} className={`text-zinc-500 transition-transform ${showROI ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {showROI && roi && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="text-sm text-zinc-500 mt-3 mb-4">If ${investAmount || 10} was invested in these artists before they were famous:</p>
                    <div className="grid grid-cols-2 gap-3">
                      {roi.comparisons?.map(comp => (
                        <div key={comp.artist} className="p-3 bg-zinc-800 rounded-xl border border-zinc-700">
                          <p className="text-xs text-zinc-500">{comp.note} ({comp.year})</p>
                          <p className="text-sm text-white font-semibold mt-1">${comp.invested} in {comp.artist}</p>
                          <p className="text-xl font-display font-black text-[#b8ff00] mt-1">${comp.today_value?.toLocaleString()}</p>
                          <p className="text-[10px] text-zinc-600 font-mono">{Math.round(comp.today_value / comp.invested).toLocaleString()}x return</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-[#b8ff00]/5 border border-[#b8ff00]/10 rounded-xl">
                      <p className="text-xs text-zinc-400 mb-2">Projected returns for ${investAmount || 10} in this project:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {roi.projections && Object.entries(roi.projections).map(([key, val]) => (
                          <div key={key} className="text-center">
                            <p className="text-sm font-bold text-white">${val}</p>
                            <p className="text-[10px] text-zinc-500">{key.replace(/_/g, ' ')}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Creator Updates */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6" data-testid="project-updates">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg text-white flex items-center gap-2"><FileText size={18} className="text-[#b8ff00]" /> Creator Updates ({updates.length})</h2>
                {user && project.creator_id === user.id && (
                  <Button onClick={() => setShowUpdateForm(!showUpdateForm)} variant="outline" size="sm" className="text-xs border-zinc-700 text-zinc-300" data-testid="toggle-update-form">
                    <PenLine size={12} className="mr-1" /> {showUpdateForm ? 'Cancel' : 'Post Update'}
                  </Button>
                )}
              </div>

              {/* Update form for creator */}
              <AnimatePresence>
                {showUpdateForm && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="p-4 bg-zinc-800 rounded-xl border border-zinc-700 mb-4 space-y-3">
                      <div className="flex gap-2">
                        {[{v:'general',l:'General'},{v:'milestone',l:'Milestone'},{v:'media',l:'Media'},{v:'financials',l:'Financials'}].map(t => (
                          <button key={t.v} onClick={() => setUpdateType(t.v)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${updateType === t.v ? 'bg-[#b8ff00] text-black' : 'bg-zinc-700 text-zinc-400'}`}>{t.l}</button>
                        ))}
                      </div>
                      <Input value={updateTitle} onChange={e => setUpdateTitle(e.target.value)} placeholder="Update title" className="bg-zinc-900 border-zinc-600 text-white" data-testid="update-title-input" />
                      <Textarea value={updateContent} onChange={e => setUpdateContent(e.target.value)} placeholder="Share progress, milestones, or news with your backers..." rows={4} className="bg-zinc-900 border-zinc-600 text-white text-sm" data-testid="update-content-input" />
                      <Button onClick={handlePostUpdate} disabled={postingUpdate} className="bg-[#b8ff00] text-black font-bold" data-testid="submit-update-btn">
                        {postingUpdate ? 'Posting...' : 'Publish Update'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Updates list */}
              <div className="space-y-3">
                {updates.map((upd, i) => {
                  const typeColors = { general: 'bg-zinc-700 text-zinc-300', milestone: 'bg-amber-500/10 text-amber-400', media: 'bg-violet-500/10 text-violet-400', financials: 'bg-emerald-500/10 text-emerald-400' };
                  const typeIcons = { milestone: Milestone, media: FileText, financials: DollarSign };
                  const TypeIcon = typeIcons[upd.update_type] || FileText;
                  return (
                    <motion.div key={upd.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      className="p-4 bg-zinc-800 rounded-xl border border-zinc-700" data-testid={`update-${upd.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${typeColors[upd.update_type] || typeColors.general}`}>
                          {upd.update_type}
                        </span>
                        <span className="text-[10px] text-zinc-600">{new Date(upd.created_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="font-bold text-sm text-white mb-1">{upd.title}</h3>
                      <p className="text-sm text-zinc-400 whitespace-pre-wrap leading-relaxed">{upd.content}</p>
                      <p className="text-[10px] text-zinc-600 mt-2">by {upd.creator_name}</p>
                    </motion.div>
                  );
                })}
                {updates.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No updates yet. {project.creator_id === user?.id ? 'Post your first update!' : 'Check back soon.'}</p>}
              </div>
            </div>

            {/* Comments */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6">
              <h2 className="font-bold text-lg text-white mb-4 flex items-center gap-2"><MessageSquare size={18} /> Comments ({comments.length})</h2>
              {user && (
                <div className="flex gap-2 mb-4">
                  <Input placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} className="flex-1 bg-zinc-800 border-zinc-700 text-white" data-testid="comment-input" />
                  <Button onClick={handleComment} className="bg-zinc-700 text-white hover:bg-zinc-600" data-testid="comment-submit">Post</Button>
                </div>
              )}
              <div className="space-y-3">
                {comments.map(c => (
                  <div key={c.id} className="p-3 bg-zinc-800 rounded-xl">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-zinc-300">{c.user_name}</span>
                      <span className="text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-zinc-400">{c.text}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No comments yet</p>}
              </div>
            </div>

            {/* Recent Backers */}
            {project.recent_backers?.length > 0 && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6">
                <h2 className="font-bold text-lg text-white mb-3">Recent Backers</h2>
                <div className="space-y-2">
                  {project.recent_backers.map(b => (
                    <div key={b.id} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg text-sm">
                      <div>
                        <span className="font-semibold text-zinc-300">{b.investor_name}</span>
                        {b.tier_name && <span className="ml-2 text-xs text-[#b8ff00]">{b.tier_name}</span>}
                      </div>
                      <span className="font-bold text-[#b8ff00]">{formatCurrency(b.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Funding Card */}
          <div className="space-y-4">
            {/* Trust Badges */}
            {trustBadges.length > 0 && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4" data-testid="trust-badges">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-white flex items-center gap-2"><Shield size={14} className="text-emerald-400" /> Trust Score</h3>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${trustScore >= 60 ? 'bg-emerald-500/10 text-emerald-400' : trustScore >= 30 ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                    {trustScore}/100
                  </span>
                </div>
                <div className="space-y-1.5">
                  {trustBadges.map((badge, i) => {
                    const badgeColors = { gold: 'border-yellow-500/30 bg-yellow-500/5 text-yellow-400', silver: 'border-zinc-500/30 bg-zinc-500/5 text-zinc-300', green: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400', info: 'border-blue-500/30 bg-blue-500/5 text-blue-400', warning: 'border-amber-500/30 bg-amber-500/5 text-amber-400', danger: 'border-red-500/30 bg-red-500/5 text-red-400', bronze: 'border-orange-500/30 bg-orange-500/5 text-orange-400' };
                    const badgeIcons = { trophy: Trophy, flame: Flame, trending: TrendingUp, users: Users, shield: Shield, target: Target, heart: Heart, alert: AlertTriangle, clock: Clock, info: Info };
                    const Icon = badgeIcons[badge.icon] || Shield;
                    return (
                      <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${badgeColors[badge.level] || badgeColors.info}`} data-testid={`trust-badge-${badge.type}`}>
                        <Icon size={12} className="flex-shrink-0" />
                        <span>{badge.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 sticky top-4" data-testid="funding-sidebar">
              <div className="text-center mb-4">
                <p className="text-4xl font-display font-black text-[#b8ff00]" data-testid="current-funding">{formatCurrency(project.current_funding)}</p>
                <p className="text-xs text-zinc-500">of {formatCurrency(project.funding_goal)} goal</p>
              </div>
              <div className="w-full bg-zinc-800 h-3 rounded-full overflow-hidden mb-3">
                <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : 'bg-gradient-to-r from-[#b8ff00] to-[#00ff88]'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div><p className="font-bold text-white">{pct.toFixed(0)}%</p><p className="text-[10px] text-zinc-500">funded</p></div>
                <div><p className="font-bold text-white">{project.backer_count}</p><p className="text-[10px] text-zinc-500">backers</p></div>
                <div><p className="font-bold text-white">{daysLeft}</p><p className="text-[10px] text-zinc-500">days left</p></div>
              </div>

              {project.status === 'active' && user && project.creator_id !== user.id && (
                <div className="space-y-3 pt-3 border-t border-zinc-800">
                  {/* Quick Amounts */}
                  <div className="grid grid-cols-3 gap-2">
                    {QUICK_AMOUNTS.map(amt => (
                      <button
                        key={amt}
                        onClick={() => setInvestAmount(String(amt))}
                        className={`py-2 rounded-lg text-sm font-bold transition-all ${String(amt) === investAmount ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                        data-testid={`quick-invest-${amt}`}
                      >
                        ${amt}
                      </button>
                    ))}
                  </div>
                  <Input type="number" min={1} placeholder="Custom amount ($)" value={investAmount} onChange={e => setInvestAmount(e.target.value)} className="text-center font-bold bg-zinc-800 border-zinc-700 text-white" data-testid="invest-amount" />
                  <Textarea placeholder="Message to creator (optional)" value={investMsg} onChange={e => setInvestMsg(e.target.value)} rows={2} className="text-sm bg-zinc-800 border-zinc-700 text-white" />
                  <Button onClick={handleInvest} disabled={investing} className="w-full bg-[#b8ff00] text-black font-bold text-lg h-12 hover:bg-[#a0e600]" data-testid="invest-btn">
                    <Rocket className="mr-2" size={18} />
                    {investing ? 'Processing...' : `Back ${investAmount ? formatCurrency(parseFloat(investAmount) || 0) : ''}`}
                  </Button>
                  <p className="text-[10px] text-zinc-600 text-center">Min $1. 5% platform fee. Funds go directly to creator.</p>
                </div>
              )}

              {!user && project.status === 'active' && (
                <Link to="/register" className="block">
                  <Button className="w-full bg-[#b8ff00] text-black font-bold h-12 hover:bg-[#a0e600]" data-testid="signup-to-invest-btn">
                    Sign Up to Back This
                  </Button>
                </Link>
              )}
              {project.status === 'funded' && <p className="text-center text-[#b8ff00] font-bold py-2">This project is fully funded!</p>}
              {project.status === 'expired' && <p className="text-center text-zinc-500 font-bold py-2">Funding period ended</p>}
            </div>

            {/* Backer Leaderboard */}
            {leaderboard.length > 0 && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4" data-testid="backer-leaderboard">
                <h3 className="font-bold text-sm text-white flex items-center gap-2 mb-3">
                  <Trophy size={14} className="text-amber-400" /> Top Backers ({totalBackers})
                </h3>
                <div className="space-y-2">
                  {leaderboard.slice(0, 10).map((backer, idx) => {
                    const rankColors = ['text-yellow-400', 'text-zinc-300', 'text-amber-600'];
                    const badgeIcons = { crown: '👑', flag: '🏁', gem: '💎', star: '⭐', heart: '❤️' };
                    return (
                      <div key={backer.user_id} className="flex items-center gap-2.5 p-2 rounded-lg bg-zinc-800/50" data-testid={`backer-${idx}`}>
                        <span className={`font-display font-bold text-sm w-5 text-center ${rankColors[idx] || 'text-zinc-600'}`}>
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white font-semibold truncate">{backer.name}</p>
                          <div className="flex items-center gap-1 flex-wrap">
                            {backer.badges.map((badge, bi) => (
                              <span key={bi} className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-300">
                                {badgeIcons[badge.icon] || '🏅'} {badge.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className="text-xs font-bold text-[#b8ff00]">{formatCurrency(backer.total_amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cross-Feature Bridges */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Bridges pageType="project" entityId={id} />
      </div>
    </div>
  );
}
